import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, computed, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngxs/store';
import { sortBy } from 'lodash';
import { TooltipIfClampedDirective } from 'ng2-app/directives/tooltip-if-clamped.directive';
import { DscrResult } from 'ng2-app/models/dscr/DscrResult.struct';
import { DscrState } from 'ng2-app/store/profit-loss-statements/dscr.state';

@Component({
  selector: 'app-profit-loss-items-table',
  template: `
    <mat-table [dataSource]="dscrResultsDataSource()">

      <mat-header-row *matHeaderRowDef="columns"></mat-header-row>
      <mat-row *matRowDef="let dscrResult; columns: columns;"></mat-row>
      <mat-footer-row *matFooterRowDef="columns"></mat-footer-row>

      <ng-container [matColumnDef]="'lender'">
        <mat-header-cell *matHeaderCellDef>
          Lender
        </mat-header-cell>
        <mat-cell *matCellDef="let dscrResult">
          <span [matTooltip]="dscrResult.lenderName" tooltipIfClamped class="tw-whitespace-nowrap tw-overflow-ellipsis">
            {{dscrResult.lenderName}}
          </span>
        </mat-cell>
        <mat-footer-cell *matFooterCellDef></mat-footer-cell>
      </ng-container>

      <ng-container [matColumnDef]="'year'">
        <mat-header-cell *matHeaderCellDef>
          Year
        </mat-header-cell>
        <mat-cell *matCellDef="let dscrResult">
          {{dscrResult.year}}
        </mat-cell>
        <mat-footer-cell *matFooterCellDef></mat-footer-cell>
      </ng-container>

      <ng-container [matColumnDef]="'businessCashFlow'">
        <mat-header-cell *matHeaderCellDef>
          Bus. Cashflow
        </mat-header-cell>
        <mat-cell *matCellDef="let dscrResult">
          {{dscrResult.businessCashFlow | currency}}
        </mat-cell>
        <mat-footer-cell *matFooterCellDef></mat-footer-cell>
      </ng-container>

      <ng-container [matColumnDef]="'businessDebtService'">
        <mat-header-cell *matHeaderCellDef>
          <span>Bus. DS</span>
          <mat-icon class="!tw-text-[18px] tw-ml-1" [matTooltip]="'Business Debt Service'">info_outline</mat-icon>
        </mat-header-cell>
        <mat-cell *matCellDef="let dscrResult">
          {{dscrResult.businessDebtService | currency}}
        </mat-cell>
        <mat-footer-cell *matFooterCellDef></mat-footer-cell>
      </ng-container>

      <ng-container [matColumnDef]="'businessDscrValue'">
        <mat-header-cell class="!tw-font-bold" *matHeaderCellDef>
          Bus. DSCR
        </mat-header-cell>
        <mat-cell *matCellDef="let dscrResult">
          <span class="tw-font-bold"
                [ngClass]="dscrResult.businessDscrValue >= 1.15 ? '!tw-text-lendio-green-600' : '!tw-text-lendio-red-500'">
            {{dscrResult.businessDscrValue}}x
          </span>
        </mat-cell>

        <mat-footer-cell *matFooterCellDef></mat-footer-cell>
      </ng-container>


      <ng-container [matColumnDef]="'personalCashFlow'">
        <mat-header-cell *matHeaderCellDef>
          <span class="tw-whitespace-nowrap">Pers. Cash</span>
          <mat-icon class="!tw-text-[18px] tw-ml-1" [matTooltip]="'Personal Cashflow'">info_outline</mat-icon>
        </mat-header-cell>
        <mat-cell *matCellDef="let dscrResult">
          {{dscrResult.personalCashFlow | currency}}
        </mat-cell>
        <mat-footer-cell *matFooterCellDef></mat-footer-cell>
      </ng-container>

      <ng-container [matColumnDef]="'personalDebtService'">
        <mat-header-cell *matHeaderCellDef>
          Pers. Debt
        </mat-header-cell>
        <mat-cell *matCellDef="let dscrResult">
          {{dscrResult.personalDebtService | currency}}
        </mat-cell>
        <mat-footer-cell *matFooterCellDef></mat-footer-cell>
      </ng-container>

      <ng-container [matColumnDef]="'globalCashFlow'">
        <mat-header-cell *matHeaderCellDef>
          <span>G. Cash</span>
          <mat-icon class="!tw-text-[18px] tw-ml-1" [matTooltip]="'Global Cashflow'">info_outline</mat-icon>
        </mat-header-cell>
        <mat-cell *matCellDef="let dscrResult">
          {{dscrResult.globalCashFlow | currency}}
        </mat-cell>
        <mat-footer-cell *matFooterCellDef></mat-footer-cell>
      </ng-container>

      <ng-container [matColumnDef]="'globalDebtService'">
        <mat-header-cell *matHeaderCellDef>
          Glob. Debt
        </mat-header-cell>
        <mat-cell *matCellDef="let dscrResult">
          {{dscrResult.globalDebtService | currency}}
        </mat-cell>
        <mat-footer-cell *matFooterCellDef>
        </mat-footer-cell>
      </ng-container>

      <ng-container [matColumnDef]="'globalDscrValue'">
        <mat-header-cell class="!tw-font-bold" *matHeaderCellDef>
          Glob. DSCR
        </mat-header-cell>
        <mat-cell *matCellDef="let dscrResult">
          <span class="tw-font-bold"
                [ngClass]="dscrResult.globalDscrValue >= 1.15 ? '!tw-text-lendio-green-600' : '!tw-text-lendio-red-500'">
            {{dscrResult.globalDscrValue}}x
          </span>
        </mat-cell>
        <mat-footer-cell *matFooterCellDef>
        </mat-footer-cell>
      </ng-container>

    </mat-table>
  `,
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    CurrencyPipe,
    MatCheckboxModule,
    MatTooltipModule,
    MatIconModule,
    TooltipIfClampedDirective,
  ],
})
export class DscrResultsTableComponent {

  dscrResults = toSignal( this.store.select( DscrState.dscrResults ) );
  dscrResultsDataSource: Signal<MatTableDataSource<DscrResult>> = computed(() => {
    const items = this.dscrResults();
    const itemsSorted = sortBy(items, ['lenderId', 'year']);
    return new MatTableDataSource(itemsSorted);
  });

  columns = [
    'lender',
    'year',
    'businessCashFlow',
    'businessDebtService',
    'businessDscrValue',
    'personalCashFlow',
    'personalDebtService',
    'globalCashFlow',
    'globalDebtService',
    'globalDscrValue',
    // 'isDscrQualified',
  ];

  constructor(
    private store: Store,
  ) {}
}
